// Ecranul de intrare — singurul lucru pe care îl vede cineva fără cont.
//
// Platforma s-a închis: screener, lentă, antrenor, tot. Cine nu e autentificat vede
// pagina asta şi nimic altceva. De aceea ea trebuie să facă două lucruri deodată —
// să spună limpede ce e înăuntru şi să nu pună piedici la intrare. Formularul e chiar
// pe ecran, nu într-o fereastră care se deschide la un clic în plus.
//
// Mişcarea e ornament, deci e construită ca ornament: pete de lumină şi o grilă care se
// animează doar prin `transform` şi `opacity`, singurele două proprietăţi pe care
// browserul le poate mişca fără să recalculeze aşezarea. Iar `prefers-reduced-motion`
// opreşte tot — cine a cerut sistemului mai puţină mişcare n-a cerut-o doar aiurea.

import { el, clear, bus } from '../core/util.js';
import { icon } from './icons.js';
import { session } from '../core/session.js';
import { toast, passwordField } from './components.js';

/** Ce e înăuntru. Scurt: cine citeşte pagina de intrare nu vrea un manual. */
const PUNCTE = [
  ['chartLine', 'Скринер на 6 бирж', 'Десятки живых графиков разом, радар объёмов, '
    + 'карта плотностей и автопоиск формаций.'],
  ['clockHistory', 'Тренажёр на истории', 'Реальный участок рынка, свеча за свечой. '
    + 'Сделки со стопами и тейками — без риска для депозита.'],
  ['comments', 'Лента и сообщество', 'Идеи с входом и стопом, разбор сетапов, '
    + 'прямые сообщения. Под своим именем и с открытой историей.'],
];

/** Cifre adevărate, nu rotunjiri de reclamă. */
const CIFRE = [['6', 'бирж'], ['2', 'рынка'], ['0', '₽ за всё']];

export function ecranIntrare() {
  let regim = session.empty ? 'register' : 'login';

  const userEl = el('input', {
    class: 'auth-input', type: 'text', placeholder: 'Ник',
    autocomplete: 'username', name: 'username',
  });
  // `type=email` scoate tastatura cu „@" pe telefon, `autocomplete=email` face sistemul
  // sa propuna adresa contului de pe aparat, iar `name` ajuta managerele mai vechi.
  // Toate trei sunt necesare, si niciuna nu e destul singura — dar nimic nu functioneaza
  // pana campurile nu stau intr-un `<form>`: fara el, browserul nici nu se uita.
  const emailEl = el('input', {
    class: 'auth-input', type: 'email', placeholder: 'Почта',
    autocomplete: 'email', name: 'email', inputmode: 'email',
    autocapitalize: 'off', spellcheck: 'false',
  });
  const { field: passField, input: passEl } = passwordField('Пароль');
  const { field: pass2Field, input: pass2El } = passwordField('Повторите пароль', 'new-password');

  const acordEl = el('input', { type: 'checkbox', class: 'auth-check' });
  const acordField = el('label', { class: 'auth-agree' }, [
    acordEl,
    el('span', {}, [
      el('span', { text: 'Я принимаю ' }),
      el('a', { class: 'auth-link', href: '/rules/', target: '_blank', rel: 'noopener',
        text: 'правила сообщества и политику данных' }),
    ]),
  ]);

  const userField = el('label', { class: 'auth-field' }, [icon('user', 13), userEl]);
  const emailField = el('label', { class: 'auth-field' }, [icon('send', 13), emailEl]);
  const errEl = el('div', { class: 'auth-error' });
  const titluEl = el('h2', { class: 'wl-form-t' });
  const hintEl = el('p', { class: 'wl-form-x' });
  const submitEl = el('button', { class: 'btn primary wl-submit', type: 'submit' });
  // Fara `type=button`, orice buton dintr-un formular trimite formularul.
  const switchEl = el('button', { class: 'auth-switch', type: 'button' });
  const forgotEl = el('button', { class: 'auth-switch', type: 'button', text: 'Забыли пароль?' });

  const sync = () => {
    const inreg = regim === 'register';
    const recuperare = regim === 'forgot';
    userField.style.display = recuperare ? 'none' : '';
    emailField.style.display = inreg || recuperare ? '' : 'none';
    passField.style.display = recuperare ? 'none' : '';
    pass2Field.style.display = inreg ? '' : 'none';
    acordField.style.display = inreg ? '' : 'none';
    forgotEl.style.display = regim === 'login' ? '' : 'none';

    titluEl.textContent = recuperare ? 'Восстановление' : inreg ? 'Создать аккаунт' : 'Вход';
    hintEl.textContent = recuperare
      ? 'Укажите почту — пришлём ссылку для смены пароля.'
      : inreg
        ? (session.empty ? 'Аккаунтов ещё нет — первый становится владельцем.'
          : 'Пароль: минимум 6 символов.')
        : 'Можно войти по нику или по почте.';
    submitEl.textContent = recuperare ? 'Прислать ссылку' : inreg ? 'Зарегистрироваться' : 'Войти';
    switchEl.textContent = inreg || recuperare ? 'Уже есть аккаунт? Войти' : 'Нет аккаунта? Создать';
    userEl.placeholder = inreg ? 'Ник' : 'Ник или почта';
    passEl.autocomplete = inreg ? 'new-password' : 'current-password';
    errEl.textContent = '';
  };

  const trimite = async () => {
    errEl.textContent = '';
    submitEl.disabled = true;
    try {
      if (regim === 'forgot') {
        await session.forgot(emailEl.value);
        toast('Если такая почта есть — письмо отправлено', 'success');
        regim = 'login'; sync();
        return;
      }
      if (regim === 'register') {
        if (passEl.value !== pass2El.value) throw new Error('Пароли не совпадают');
        if (!acordEl.checked) throw new Error('Нужно принять правила сообщества');
        await session.register(userEl.value.trim(), emailEl.value.trim(), passEl.value);
      } else {
        await session.login(userEl.value.trim(), passEl.value);
      }
      // Nu redesenăm nimic aici: `session:change` e semnalul, iar aplicaţia îl ascultă
      // deja. Două căi care fac acelaşi lucru se despart mai devreme sau mai târziu.
      toast(`Привет, ${session.user.username}`, 'success');
    } catch (err) {
      errEl.textContent = err.message;
      if (regim === 'register' && err.message.includes('совпад')) pass2El.select();
      else if (regim !== 'forgot') passEl.select();
    } finally {
      submitEl.disabled = false;
    }
  };

  // Trimiterea trece prin `submit`-ul formularului; clicul pe buton il declanseaza
  // singur, iar browserul stie astfel ca s-a intrat in cont si intreaba daca sa retina
  // parola. Un `onclick` obisnuit nu i-ar fi spus nimic.

  switchEl.onclick = () => { regim = regim === 'login' ? 'register' : 'login'; sync(); };
  forgotEl.onclick = () => { regim = 'forgot'; sync(); };
  // „Enter" il trateaza formularul; un ascultator in plus pe fiecare camp ar trimite
  // de doua ori — o data de la tasta, o data de la `submit`.


  const radacina = el('div', { class: 'wl' }, [
    el('div', { class: 'wl-bg', 'aria-hidden': 'true' }, [
      el('span', { class: 'wl-orb o1' }),
      el('span', { class: 'wl-orb o2' }),
      el('span', { class: 'wl-orb o3' }),
      el('span', { class: 'wl-grid' }),
    ]),

    el('div', { class: 'wl-corp' }, [
      el('section', { class: 'wl-stanga' }, [
        el('span', { class: 'wl-marca' }, [
          el('span', { class: 'tabs-home-s', text: 'S' }),
          el('span', { class: 'tabs-home-x', text: 'X' }),
          el('span', { class: 'wl-marca-t', text: 'ScalpScreener' }),
        ]),
        el('h1', { class: 'wl-h1', text: 'Скринер, тренажёр и сообщество — под одним аккаунтом' }),
        el('p', { class: 'wl-lead', text: 'Всё, что нужно скальперу, в одном окне. '
          + 'Без подписок и без карты — аккаунт нужен только чтобы всё это открыть.' }),

        el('div', { class: 'wl-puncte' }, PUNCTE.map(([ico, titlu, text], i) => el('div', {
          class: 'wl-punct', style: `--intarziere:${i * 90}ms`,
        }, [
          el('span', { class: 'wl-punct-ico' }, [icon(ico, 15)]),
          el('div', {}, [
            el('span', { class: 'wl-punct-t', text: titlu }),
            el('span', { class: 'wl-punct-x', text }),
          ]),
        ]))),

        el('div', { class: 'wl-cifre' }, CIFRE.map(([n, t]) => el('div', { class: 'wl-cifra' }, [
          el('span', { class: 'wl-cifra-n', text: n }),
          el('span', { class: 'wl-cifra-t', text: t }),
        ]))),
      ]),

      el('section', { class: 'wl-card' }, [
        titluEl, hintEl,
        // Un `<form>` adevarat, nu doar campuri asezate unul sub altul. Browserul si
        // sistemul de pe telefon se uita dupa el ca sa propuna adresa si parola
        // salvate, si tot el face ca „Enter" de pe tastatura sa insemne „intra".
        el('form', {
          class: 'wl-form',
          onsubmit: (e) => { e.preventDefault(); trimite(); },
        }, [
          userField, emailField, passField, pass2Field, acordField,
          errEl, submitEl,
        ]),
        el('div', { class: 'auth-links' }, [switchEl, forgotEl]),
        el('p', { class: 'wl-jos' }, [
          icon('lock', 11),
          el('span', { text: 'Пароль хранится хэшем. Почта нужна только для '
            + 'подтверждения и восстановления доступа.' }),
        ]),
      ]),
    ]),
  ]);

  sync();
  setTimeout(() => userEl.focus(), 80);
  return radacina;
}
